import { useCallback } from 'react';

/**
 * Scene 页面专用的字段输入处理器 Hook
 * 
 * 注意：鼠标处理逻辑已移至统一鼠标交互系统 (useUnifiedMouseInteraction)
 * 此 Hook 现在仅用于提供 onEnterEditing 回调机制
 * 
 * 实现方式：通过元素属性存储 onEnterEditing 回调，供统一系统在进入编辑时调用
 * 
 * @example
 * ```tsx
 * const createFieldHandlers = useSceneFieldInputHandlers();
 * 
 * <input
 *   {...createFieldHandlers({
 *     onEnterEditing: (element) => {
 *       // 初始化本地输入值等特殊逻辑
 *     }
 *   })}
 * />
 * ```
 */
export const useSceneFieldInputHandlers = () => {
  return useCallback((options: { 
    onEnterEditing?: (element: HTMLElement) => void;
    selectText?: boolean;
  } = {}) => {
    const { onEnterEditing, selectText = false } = options;

    // 如果没有 onEnterEditing 回调和 selectText，返回空对象（不需要任何处理）
    if (!onEnterEditing && !selectText) {
      return {};
    }

    // 通过 ref 回调在元素挂载时存储 onEnterEditing 回调和 selectText 配置
    // 统一系统会在进入编辑时检查并调用此回调
    return {
      ref: (element: HTMLInputElement | HTMLTextAreaElement | null) => {
        if (element) {
          // 存储回调到元素属性
          if (onEnterEditing) {
            (element as any).__onEnterEditing = onEnterEditing;
          }
          // 存储 selectText 配置到元素属性
          if (selectText) {
            (element as any).__selectText = true;
          }
        }
      },
    };
  }, []);
};

